import { useEffect, useMemo, useState } from "react";
import Creatable from "react-select/creatable";
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import { Select } from "components/ui";
import { useTranslation } from "translation/useTranslation";

/**
 * The current value is always kept as an option even if not among the fetched values.
 */
export default function DepartmentSelect({ value, onChange, error }) {
  const { translate } = useTranslation();
  const [values, setValues] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, status } = await api.get(`/settings/fields/department/values`);
        if (active && status === 200) setValues(data.data || []);
      } catch (err) {
        Sentry.captureException(err);
      }
    })();
    return () => { active = false; };
  }, []);

  const options = useMemo(() => {
    const arr = (values || []).map((val) => ({ value: val, label: val }));
    if (value && !arr.some((o) => o.value === value)) arr.push({ value, label: value });
    return arr;
  }, [values, value]);

  return (
    <>
      <div style={{ display: 'contents' }} data-testid="department-select">
        <Select
          as={Creatable}
          options={options}
          error={!!error}
          onChange={(opt) => onChange(opt?.value ?? '')}
          value={options.find((o) => o.value === value) || null}
          isClearable
          placeholder={translate('form.select_placeholder')}
          noOptionsMessage={() => translate('form.no_options_createable')}
          formatCreateLabel={(val) => translate('form.createable_label') + ` "${val}"`}
        />
      </div>
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </>
  );
}
